import React, { useEffect, useState } from 'react';
import { ModalShell } from '../ds/ModalShell';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { buildingsApi, CaseNumbersResponse } from '../services/api';
import { caseNumberRows, caseNumbersCsv, caseNumbersOnly, caseNumbersText } from '../utils/caseNumbers';
import { saveBlob } from '../utils/download';
import { budynkiLabel, pluralPl } from '../utils/plural';

export interface CaseNumbersModalProps {
  /** Budynki do zestawienia; `null` = modal zamknięty. */
  buildingIds: string[] | null;
  onClose: () => void;
}

const banner = (kind: 'error' | 'ok'): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-label)',
  background: kind === 'error' ? 'var(--status-deny-bg)' : 'var(--status-ok-bg)',
  border: `var(--border-width) solid ${kind === 'error' ? 'var(--status-deny-bar)' : 'var(--status-ok-bar)'}`,
  color: kind === 'error' ? 'var(--status-deny)' : 'var(--status-ok)',
});

const td: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderBottom: 'var(--border-width) solid var(--border)',
  color: 'var(--text-primary)',
  verticalAlign: 'top',
};
const th: React.CSSProperties = { ...td, color: 'var(--text-secondary)', fontWeight: 'var(--weight-semibold)' as any, textAlign: 'left' };

const numeryLabel = (n: number) => `${n} ${pluralPl(n, ['numer', 'numery', 'numerów'])}`;

/**
 * „Numery protokołów" (A22, lista Bronka 2026-10): numery spraw CEEB zaznaczonych budynków —
 * z bieżącego roku, a bez niego ostatni znany (zawsze z rokiem). Unikalne, po adresie; budynki bez
 * numeru osobno. Udostępnienie: schowek (lista z adresami albo same numery) albo CSV dla Excela.
 */
export default function CaseNumbersModal({ buildingIds, onClose }: CaseNumbersModalProps) {
  const [data, setData] = useState<CaseNumbersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copyMsg, setCopyMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);

  // Klucz zamiast tablicy: rodzic podaje nowy literał co render, a pobieramy tylko przy zmianie wyboru.
  const key = buildingIds && buildingIds.length ? JSON.stringify(buildingIds) : '';
  useEffect(() => {
    setCopyMsg(null);
    if (!key) { setData(null); setError(null); return undefined; }
    let stale = false; // guard anty-stale: zamknięcie/zmiana wyboru w trakcie nie nadpisze wyniku
    setLoading(true);
    setData(null);
    setError(null);
    buildingsApi.caseNumbers(JSON.parse(key))
      .then((res) => { if (!stale) setData(res); })
      .catch((e) => { if (!stale) setError(e instanceof Error ? e.message : 'Nie udało się pobrać numerów protokołów'); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [key]);

  const { rows, withoutNumber } = data ? caseNumberRows(data) : { rows: [], withoutNumber: [] };
  const empty = rows.length === 0 && withoutNumber.length === 0;

  const copy = async (text: string, okText: string) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('no clipboard');
      await navigator.clipboard.writeText(text);
      setCopyMsg({ kind: 'ok', text: okText });
    } catch {
      // Schowek bywa niedostępny (http bez TLS, brak zgody przeglądarki) — CSV działa zawsze.
      setCopyMsg({ kind: 'error', text: 'Przeglądarka nie pozwoliła skopiować — użyj „Pobierz CSV".' });
    }
  };

  const exportCsv = () => {
    const blob = new Blob([caseNumbersCsv(rows, withoutNumber)], { type: 'text/csv;charset=utf-8' });
    saveBlob(blob, `numery_protokolow_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <ModalShell
      open={!!key}
      title="Numery protokołów"
      width={720}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Zamknij</Button>
          <Button
            variant="secondary"
            icon="clipboard"
            onClick={() => copy(caseNumbersOnly(rows), `Skopiowano ${numeryLabel(rows.length)}.`)}
            disabled={rows.length === 0}
          >
            Kopiuj same numery
          </Button>
          <Button
            variant="secondary"
            icon="clipboard"
            onClick={() => copy(caseNumbersText(rows, withoutNumber), 'Skopiowano listę z adresami — wklej ją np. w mail.')}
            disabled={!data || empty}
          >
            Kopiuj
          </Button>
          <Button variant="primary" icon="download" onClick={exportCsv} disabled={!data || empty}>
            Pobierz CSV (Excel)
          </Button>
        </>
      }
    >
      <div data-testid="case-numbers" style={{ display: 'grid', gap: 'var(--space-4)', fontFamily: 'var(--font-sans)' }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          Numery spraw CEEB z bieżącego roku{data ? ` (${data.currentYear})` : ''}; budynek bez protokołu w tym roku —
          ostatni znany numer z rokiem. Każdy numer występuje raz, kolejność po adresie. Numery i statusy pochodzą
          z nocnego odświeżenia danych z CEEB — zmiany z dzisiaj mogą jeszcze nie być widoczne.
        </p>
        {loading && <div style={{ color: 'var(--text-secondary)' }}>Ładowanie…</div>}
        {error && (
          <div role="alert" style={banner('error')}>
            <Icon name="triangle-alert" size={18} />
            <span>{error}</span>
          </div>
        )}
        {copyMsg && (
          <div role={copyMsg.kind === 'error' ? 'alert' : 'status'} style={banner(copyMsg.kind)}>
            <Icon name={copyMsg.kind === 'error' ? 'triangle-alert' : 'check'} size={18} />
            <span>{copyMsg.text}</span>
          </div>
        )}
        {data && data.skipped.length > 0 && (
          <div style={{ color: 'var(--status-rec)', fontSize: 'var(--text-label)' }}>
            Pominięto {budynkiLabel(data.skipped.length)} (brak dostępu lub budynek nie istnieje).
          </div>
        )}
        {data && rows.length > 0 && (
          <div style={{ overflow: 'auto', maxHeight: '50vh' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--text-label)' }}>
              <thead>
                <tr><th style={th}>Adres</th><th style={th}>Numer sprawy</th><th style={th}>Rok</th><th style={th}>Status w CEEB</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.caseNumber}>
                    <td style={td}>{r.address}</td>
                    <td style={{ ...td, fontWeight: 'var(--weight-semibold)' as any, fontVariantNumeric: 'tabular-nums' }}>{r.caseNumber}</td>
                    <td style={{ ...td, color: r.current ? 'var(--text-primary)' : 'var(--status-rec)' }}>
                      {r.year ?? '—'}{r.current ? '' : ' (ostatni znany)'}
                    </td>
                    <td style={{ ...td, color: 'var(--text-secondary)' }}>{r.status || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && withoutNumber.length > 0 && (
          <section aria-label="Budynki bez numeru sprawy">
            <h3 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-body)', color: 'var(--text-heading)' }}>
              Bez numeru sprawy CEEB ({budynkiLabel(withoutNumber.length)})
            </h3>
            <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
              {withoutNumber.map((a, i) => <li key={`${i}-${a}`}>{a}</li>)}
            </ul>
          </section>
        )}
        {data && empty && <div style={{ color: 'var(--text-secondary)' }}>Brak budynków do zestawienia.</div>}
      </div>
    </ModalShell>
  );
}
